<script lang="ts">
	import { Accordion, AccordionItem, Dropdown, DropdownGroup, DropdownItem, Search } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { SvelteSet } from "svelte/reactivity";
	import { twMerge } from "tailwind-merge";
	import { ContactSchemaPropertyFilterSchema, type ContactSchemaPropertyFilter, type SchemaProperty } from "#gen/abacus/v2/abacus_pb.js";
	import { Order, PaginationArgumentsSchema, type PaginationArguments, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
	import { ChoiceFieldPropertiesSchema, FieldPropertiesSchema, FieldType, ModelFieldSchema, type Field } from "#gen/forms/v1/field_pb.js";
	import type { Form } from "#gen/forms/v1/forms_pb.js";
	import type { Workspace } from "#gen/identities/v1/identities_pb.js";
	import type { AbacusClient } from "#lib/api/abacus.js";
	import { DEFAULT_PAGE_SIZE } from "#lib/api/api.js";
	import { clearPagination } from "#lib/api/pagination.js";
	import Checkbox from "#lib/components/Checkbox.svelte";
	import DataTypeBadge from "#lib/components/DataTypeBadge.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import InfiniteScroll from "#lib/components/InfiniteScroll.svelte";
	import P from "#lib/components/P.svelte";
	import ContactPropertyMismatchDialog from "#lib/components/page/components/form/ContactPropertyMismatchDialog.svelte";
	import { getDataType, getValidFieldTypes, getValidPropertyTypeForField, isStringField } from "#lib/components/page/components/form/fieldTypes.js";
	import { workspaceAdminPath } from "#lib/path.js";
	import PermissionGate from "#lib/permissions/PermissionGate.svelte";
	import { create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import { browser } from "$app/env";
	import { goto } from "$app/navigation";
	import { resolve } from "$app/paths";
	import type { Writable } from "svelte/store";

	interface Props {
		field: Field;
		formData?: Form;
		onChange?: (field: Field) => void;
	}

	const dropdownKey = "select-contact-property";
	const workspace: Writable<Workspace> = getContext("workspace");
	const abacusClient: AbacusClient = getContext("abacusClient");
	const lastError: Writable<unknown> = getContext("lastError");

	let { field, formData, onChange }: Props = $props();

	const filteredProperties = $derived(getAvailableProperties(field, formData));

	let contactProperties: SchemaProperty[] = $state([]);
	let controller: AbortController | undefined = undefined;
	const pagination: PaginationArguments = $state(create(PaginationArgumentsSchema, { first: DEFAULT_PAGE_SIZE, orderBy: "label", order: Order.ASC }));
	const filter: ContactSchemaPropertyFilter = $state(create(ContactSchemaPropertyFilterSchema, { labelPrefix: "" }));
	let pageInfo: PaginationPageInfo | undefined = $state(undefined);
	let open = $state(false);
	let showMismatchDialog = $state(false);
	let pendingPropertyToConnect: SchemaProperty | undefined = $state(undefined);

	// svelte-ignore state_referenced_locally
	let checked = $state(Boolean(field.modelFieldBinding));

	$effect(() => {
		if (browser && checked) {
			search(filter);
		}
	});

	async function search(filter: ContactSchemaPropertyFilter) {
		clearPagination(pagination);
		filter.type = isStringField(field) ? undefined : getValidPropertyTypeForField(field.fieldType, field.properties?.properties.case === "choice" ? field.properties?.properties.value?.choiceType : undefined);
		await fetchContactSchema(filter);
	}

	async function fetchContactSchema(filter: ContactSchemaPropertyFilter, scroll: boolean = false) {
		controller?.abort();
		controller = new AbortController();
		try {
			const res = await abacusClient.getContactSchema(
				$workspace.orgId,
				$workspace.id,
				filter,
				create(PaginationArgumentsSchema, {
					first: DEFAULT_PAGE_SIZE,
					after: scroll ? pageInfo?.endCursor : undefined,
					order: Order.ASC,
					orderBy: "label",
				}),
				{ signal: controller.signal },
			);
			if (res) {
				if (scroll) {
					contactProperties = [...contactProperties, ...(res.schema?.properties ?? [])];
				} else {
					contactProperties = res.schema?.properties ?? [];
				}
				pageInfo = res.pageInfo;
			}
		} catch (e) {
			$lastError = e;
		}
	}

	function getAvailableProperties(thisField: Field, form: Form | undefined) {
		const usedKeys = new SvelteSet(
			form?.fields
				?.filter(otherField => otherField.id !== thisField.id && otherField.modelFieldBinding)
				.map(otherField => otherField.modelFieldBinding?.modelFieldKey)
				.filter(Boolean) || [],
		);

		return contactProperties.filter(property => !usedKeys.has(property.key));
	}

	function enableFieldMapping(enabled: boolean) {
		if (!enabled && field.modelFieldBinding) {
			const resolvedField = field;
			resolvedField.modelFieldBinding = undefined;
			onChange?.(resolvedField);
		}
		checked = enabled;
	}

	function onMapToProperty(item: SchemaProperty) {
		const fieldTypes = getValidFieldTypes(item.type);
		if (!fieldTypes.map(f => f.type).includes(field.fieldType) || (field.properties?.properties.case === "choice" && !fieldTypes.map(f => f.choiceType).includes(field.properties?.properties.value?.choiceType))) {
			pendingPropertyToConnect = item;
			showMismatchDialog = true;
		} else {
			const resolvedField = field;
			resolvedField.modelFieldBinding = create(ModelFieldSchema, {
				modelKey: "contact",
				modelFieldKey: item.key,
			});
			onChange?.(resolvedField);
		}
		open = false;
	}

	async function createNewProperty() {
		open = false;
		const defaultType = getValidPropertyTypeForField(field.fieldType, field.properties?.properties.case === "choice" ? field.properties?.properties.value?.choiceType : undefined);
		await goto(resolve(workspaceAdminPath($workspace.orgId, $workspace.id, "", `create=${defaultType}`, "contact-properties")), { refreshAll: true });
	}

	function updateFieldType() {
		if (pendingPropertyToConnect) {
			const resolvedField = field;
			const validFieldTypes = getValidFieldTypes(pendingPropertyToConnect.type);
			const selectedType = validFieldTypes[0];
			if (selectedType) {
				if (selectedType.type === FieldType.CHOICE) {
					resolvedField.properties = create(FieldPropertiesSchema, { properties: { case: "choice", value: create(ChoiceFieldPropertiesSchema, { choiceType: selectedType.choiceType, choices: [{ value: "Option 1" }] }) } });
					resolvedField.fieldType = FieldType.CHOICE;
				} else {
					resolvedField.fieldType = selectedType?.type;
				}
				resolvedField.modelFieldBinding = create(ModelFieldSchema, {
					modelKey: "contact",
					modelFieldKey: pendingPropertyToConnect?.key,
				});
				onChange?.(resolvedField);
			}
			pendingPropertyToConnect = undefined;
		}
	}
</script>

<div class="flex h-10.5 flex-row gap-3" class:items-center={field.immutable && !field.description}>
	<Checkbox disabled={field.immutable} class="shrink-0 font-normal" {checked} on:change={e => enableFieldMapping((e.target as HTMLInputElement).checked)} data-action-name="form-field-contact-property-checkbox">Contact property</Checkbox>

	{#if checked}
		<Button color="alternative" class="w-full px-2.5 py-2" disabled={field.immutable} data-action-name={dropdownKey} aria-controls={dropdownKey} aria-haspopup="true" aria-expanded={open}>
			<P class={twMerge("mr-auto truncate text-left text-sm font-normal whitespace-nowrap", field.modelFieldBinding ? "text-gray-900" : "text-gray-500")}>
				{field.modelFieldBinding ? (contactProperties.find(property => property.key === field.modelFieldBinding?.modelFieldKey)?.label ?? field.modelFieldBinding?.modelFieldKey) : "Select a contact property"}
			</P>

			<Icon name="chevron-down" class="ml-2 h-2.5 w-2.5 flex-none" />
		</Button>
		<Dropdown id={dropdownKey} bind:isOpen={open} class="mt-2 w-150" transitionParams={{ duration: 0 }}>
			<DropdownGroup class="py-1">
				{#if filteredProperties.length === 0 && !filter.labelPrefix?.length}
					<!-- empty state -->
					<div class="flex flex-col items-center gap-2 p-4">
						<Icon name="circle-stack" class="h-5 w-5 shrink-0" />
						<P size="sm" class="font-semibold text-black">All contact properties are in use</P>
						<PermissionGate action="adminWorkspace">
							<P size="xs" class="text-center text-black">Create a new one to track additional customer data.</P>
							<Button size="xs" class="px-3 py-2" color="alternative" data-action-name="create-contact-property" on:click={createNewProperty}>
								<Icon name="plus" class="mr-1 inline-block h-5 w-5" />
								Create new
							</Button>
							<svelte:fragment slot="fallback">
								<P size="xs" class="font-normal">Contact your admin to track something new.</P>
							</svelte:fragment>
						</PermissionGate>
						<Accordion flush class="w-full rounded-md bg-gray-100">
							<AccordionItem classes={{ button: "py-0 px-4 border-b-0", content: "w-full shrink-0 py-0 pb-1 border-b-0" }}>
								{#snippet header()}
									<div class="flex w-full flex-row items-center gap-2 py-3">
										<Icon name="light-bulb" class="h-4 w-4 " />
										<P size="xs" class="font-medium">What's a contact property?</P>
									</div>
								{/snippet}
								<div class="px-4">
									<P size="xs" class="mb-2 text-gray-500 dark:text-gray-400">A contact property is a custom field that tracks customer contact info and can automatically sync with your CRM.</P>
								</div>
							</AccordionItem>
						</Accordion>
					</div>
				{:else}
					<div class="flex w-full flex-col border-b">
						<div class="p-2">
							<Search placeholder="Search" data-action-name="search-contact-properties" size="md" bind:value={filter.labelPrefix} autofocus={true} />
						</div>
						{#if filteredProperties.length === 0 && filter.labelPrefix}
							<div class="p-4 text-center">
								<P size="sm" class="text-center text-gray-500">No available contact properties matching "{filter.labelPrefix}"</P>
							</div>
						{:else}
							<div class="max-h-75 min-h-0 flex-none gap-4 overflow-x-hidden overflow-y-auto px-0">
								{#each filteredProperties as item (item.key)}
									<DropdownItem class="flex w-full items-center gap-4" onclick={() => onMapToProperty(item)} data-action-name="map-to-property" data-action-title={item.key}>
										<div class="flex grow flex-col truncate">
											<P size="sm" class="font-medium text-gray-700">{item.label}</P>
											<P size="sm" class="line-clamp-2 truncate" secondary>{item.key}</P>
										</div>
										<DataTypeBadge dataType={getDataType(item.type)} />
									</DropdownItem>
								{/each}
								<InfiniteScroll hasMore={pageInfo?.hasNextPage} threshold={0.1} onLoadMore={() => fetchContactSchema(filter, true)} />
							</div>
						{/if}
					</div>
					<PermissionGate action="adminWorkspace">
						<DropdownItem classes={{ li: "flex" }} class="w-full py-4" onclick={createNewProperty} data-action-name="create-contact-property">
							<div class="flex flex-row items-center">
								<Icon name="plus" class="mr-1 inline-block h-5 w-5 font-normal" />
								<P size="sm" class="font-medium text-gray-800">Create new property</P>
							</div>
						</DropdownItem>
						<svelte:fragment slot="fallback">
							<div class="align-center px-8 py-3">
								<P size="sm" class="text-center">
									<strong>Tracking something different?</strong> Contact your admins to create a new contact property.
								</P>
							</div>
						</svelte:fragment>
					</PermissionGate>
				{/if}
			</DropdownGroup>
		</Dropdown>
	{/if}
</div>
{#if showMismatchDialog}
	<ContactPropertyMismatchDialog bind:open={showMismatchDialog} {pendingPropertyToConnect} on:confirm={updateFieldType} />
{/if}
